import { useEffect, useMemo, useState } from 'react';
import type { TraversalParams } from '@ardoq/api-types';
import { modal } from '@ardoq/modal';
import { ViewpointBuilder } from 'viewpointBuilder/ViewpointBuilder';
import {
  OpenViewpointBuilderArgs,
  ViewpointBuilderCommands,
  ViewpointBuilderContext,
  ViewpointBuilderViewState,
} from 'viewpointBuilder/types';
import type { ViewpointBuilderTab } from 'viewpointBuilder/viewpointBuilderNavigation/types';
import { extractTraversalParamsFromViewpointBuilderState } from 'viewpointBuilder/utils';
import { getViewpointBuilderCommands } from 'viewpointBuilder/viewpointBuilderCommands';
import { viewpointBuilderTracking } from 'viewpointBuilder/viewpointBuilderTracking';
import { getInitialViewpointBuilderState } from './getInitialViewpointBuilderState';
import { subscribeToViewpointBuilderStreamAndRoutines } from './subscribeToViewpointBuilderStreamAndRoutines';
import { v4 as uuid } from 'uuid';

type ViewpointBuilderSessionArgs = {
  activeTab: ViewpointBuilderTab;
  context: ViewpointBuilderContext;
  initialConfiguration: OpenViewpointBuilderArgs['initialConfiguration'];
  folderId: string | null;
};

type ViewpointBuilderSession = {
  state: ViewpointBuilderViewState;
  viewpointBuilderCommands: ViewpointBuilderCommands;
};

const useViewpointBuilderSession = (
  args: ViewpointBuilderSessionArgs
): ViewpointBuilderSession => {
  const [{ activeTab, context, initialConfiguration, folderId }] = useState(
    () => args
  );
  const [namespace] = useState(() => uuid());
  const viewpointBuilderCommands = useMemo(
    () => getViewpointBuilderCommands(namespace),
    [namespace]
  );
  const [state, setState] = useState<ViewpointBuilderViewState>(() =>
    getInitialViewpointBuilderState(namespace)
  );

  useEffect(() => {
    const unsubscribeStreamAndRoutinesAndCleanUp =
      subscribeToViewpointBuilderStreamAndRoutines({
        namespace,
        activeTab,
        context,
        initialConfiguration,
        folderId,
        viewpointBuilderCommands,
        setState,
      });
    return unsubscribeStreamAndRoutinesAndCleanUp;
  }, [
    namespace,
    viewpointBuilderCommands,
    setState,
    activeTab,
    context,
    initialConfiguration,
    folderId,
  ]);

  return { state, viewpointBuilderCommands };
};

export const openViewpointBuilder = ({
  activeTab = 'SELECT_CONTEXT_COMPONENT_INSTANCES_TAB',
  context = 'editSubgraph',
  initialConfiguration,
  folderId,
}: OpenViewpointBuilderArgs) => {
  return modal(closeModal => {
    const { state, viewpointBuilderCommands } = useViewpointBuilderSession({
      activeTab,
      context,
      initialConfiguration,
      folderId: folderId ?? null,
    });

    return (
      <ViewpointBuilder
        closeViewpointBuilder={() => closeModal(false)}
        state={state}
        commands={viewpointBuilderCommands}
        context={context}
      />
    );
  });
};

export type OpenViewpointBuilderForTraversalArgs = {
  activeTab: ViewpointBuilderTab;
  context: ViewpointBuilderContext;
  initialConfiguration: OpenViewpointBuilderArgs['initialConfiguration'];
  onApply: (traversal: TraversalParams) => void;
};

const withApplyAction = (
  state: ViewpointBuilderViewState,
  onApply: (traversal: TraversalParams) => void
): ViewpointBuilderViewState => ({
  ...state,
  primaryButtonConfig: {
    ...state.primaryButtonConfig,
    primaryAction: closeViewpointBuilder => {
      onApply(extractTraversalParamsFromViewpointBuilderState(state));
      viewpointBuilderTracking.trackOpenDataSet(state);
      viewpointBuilderTracking.trackDatasetApplied(state);
      closeViewpointBuilder();
    },
  },
});

/**
 * Opens the viewpoint builder and hands the confirmed traversal to `onApply`,
 * instead of opening it in viewpoint mode.
 */
export const openViewpointBuilderForTraversal = ({
  activeTab,
  context,
  initialConfiguration,
  onApply,
}: OpenViewpointBuilderForTraversalArgs) =>
  modal(closeModal => {
    const { state, viewpointBuilderCommands } = useViewpointBuilderSession({
      activeTab,
      context,
      initialConfiguration,
      folderId: null,
    });

    return (
      <ViewpointBuilder
        closeViewpointBuilder={() => closeModal(false)}
        state={withApplyAction(state, onApply)}
        commands={viewpointBuilderCommands}
        context={context}
      />
    );
  });
